import React from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '../../utils/cn';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  indeterminate?: boolean;
  label?: string;
  helperText?: string;
  error?: boolean;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, indeterminate, label, helperText, error, ...props }, ref) => {
    const checkboxRef = React.useRef<HTMLInputElement>(null);
    
    React.useImperativeHandle(ref, () => checkboxRef.current!);
    
    React.useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = indeterminate || false;
      }
    }, [indeterminate]);

    const CheckboxComponent = (
      <div className="relative">
        <input
          type="checkbox"
          className="sr-only"
          ref={checkboxRef}
          {...props}
        />
        <div
          className={cn(
            'flex h-4 w-4 items-center justify-center rounded border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            props.checked || indeterminate
              ? 'bg-primary text-primary-foreground'
              : 'bg-background',
            props.disabled && 'cursor-not-allowed opacity-50',
            error && 'border-red-500',
            className
          )}
        >
          {props.checked && !indeterminate && (
            <Check className="h-3 w-3" />
          )}
          {indeterminate && (
            <Minus className="h-3 w-3" />
          )}
        </div>
      </div>
    );

    if (label || helperText) {
      return (
        <div className="flex items-start space-x-2">
          {CheckboxComponent}
          <div className="flex-1">
            {label && (
              <label
                htmlFor={props.id}
                className={cn(
                  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                  error && 'text-red-500'
                )}
              >
                {label}
              </label>
            )}
            {helperText && (
              <p className={cn(
                'mt-1 text-sm',
                error ? 'text-red-500' : 'text-muted-foreground'
              )}>
                {helperText}
              </p>
            )}
          </div>
        </div>
      );
    }

    return CheckboxComponent;
  }
);

Checkbox.displayName = 'Checkbox';
